const API_BASE = "/api";

/**
 * Error thrown by API helpers when the server returns a non-2xx status.
 * Carries the parsed JSON body, status, and the server-generated requestId
 * (when present) so error toasts can surface a copyable debug bundle that
 * correlates to CloudWatch logs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly requestId: string | null;
  readonly path: string;
  readonly method: string;

  constructor(args: {
    status: number;
    message: string;
    body: unknown;
    requestId: string | null;
    path: string;
    method: string;
  }) {
    super(args.message);
    this.name = "ApiError";
    this.status = args.status;
    this.body = args.body;
    this.requestId = args.requestId;
    this.path = args.path;
    this.method = args.method;
  }
}

export function extractRequestId(res: Response, body: unknown): string | null {
  const headerId = res.headers.get("x-request-id");
  if (headerId) return headerId;
  if (body && typeof body === "object" && "requestId" in body) {
    const v = (body as { requestId?: unknown }).requestId;
    return typeof v === "string" ? v : null;
  }
  return null;
}

async function readErrorBody(res: Response): Promise<unknown> {
  return res.json().catch(() => ({ error: `API error: ${res.status}` }));
}

function errorMessage(body: unknown, status: number): string {
  const obj = body as { error?: string; message?: string } | null;
  return obj?.message ?? obj?.error ?? `API error: ${status}`;
}

async function throwIfNotOk(res: Response, path: string, method: string): Promise<void> {
  if (res.ok) return;
  const body = await readErrorBody(res);
  throw new ApiError({
    status: res.status,
    message: errorMessage(body, res.status),
    body,
    requestId: extractRequestId(res, body),
    path,
    method,
  });
}

export async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  await throwIfNotOk(res, path, "GET");
  return res.json() as Promise<T>;
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> {
  const baseHeaders: Record<string, string> = body ? { "Content-Type": "application/json" } : {};
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { ...baseHeaders, ...(headers ?? {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  await throwIfNotOk(res, path, "POST");
  return res.json() as Promise<T>;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, path, "PUT");
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(res, path, "PATCH");
  return res.json() as Promise<T>;
}

export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, { method: "DELETE" });
  if (res.ok || res.status === 204) return;
  const body = await readErrorBody(res);
  throw new ApiError({
    status: res.status,
    message: errorMessage(body, res.status),
    body,
    requestId: extractRequestId(res, body),
    path,
    method: "DELETE",
  });
}

/**
 * Upload helper for multipart/form-data. Throws ApiError with requestId so
 * upload failures surface the same copyable debug context as JSON endpoints.
 */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", body: formData });
  await throwIfNotOk(res, path, "POST");
  return res.json() as Promise<T>;
}
